// WHAT: The piece sets there are, and which army in each plays which colour.
// HOW:  One entry per folder under src/chesspieces/. A model's filename names
//       its army and its piece — `anzac_pawn.glb` — and this is where "anzac"
//       becomes White. The original set names its colours directly
//       (`pawn_white.glb`), so its armies are simply called white and black.
// WHY:  A filename can say which army a figure belongs to, and should: that is
//       what the modeller knows while making it. It cannot say which side of a
//       chessboard that army sits on, because that is a decision about the
//       game, and it belongs in one place rather than in twenty-four filenames.
//
//       Adding a set is adding its folder of models and one line here. A set is
//       offered to players only once all twelve pieces exist — see
//       `isCompleteSet` — so a half-made set can live in the repository, and be
//       previewed with `?pieces=<set>`, without anybody else seeing it.

import type { PieceType } from '@domain/chess/types';

export interface PieceSetDef {
  /** The army that plays White, as it is spelled in the filenames. */
  readonly white: string;
  /** The army that plays Black. */
  readonly black: string;
  /**
   * Degrees a piece was modelled turned from the set's usual front, for the
   * pieces that were. A standing figure is made facing forward; a field gun is
   * usually made along its length, barrel out to the side, and left alone it
   * fires down the rank instead of at the enemy. Positive turns it
   * anticlockwise seen from above.
   */
  readonly turn?: Readonly<Partial<Record<PieceType, number>>>;
  /**
   * The folder its models are in, when that is not the set's own name. One war
   * can have more armies than one board has sides: the WW2 folder holds the
   * Americans, the Germans and the Russians, and each front is a set drawing
   * its pair from it.
   */
  readonly folder?: string;
}

export const PIECE_SETS: Readonly<Record<string, PieceSetDef>> = {
  medieval: { white: 'white', black: 'black' },
  /*
    Gallipoli's armies, used for every First World War board for now. ANZAC is
    White: the side that landed and attacked moves first, and the Anzac Cove
    board is turned so that White's back rank is the beach they came ashore on.
  */
  ww1: {
    white: 'anzac',
    black: 'ottoman',
    // Both field guns were modelled barrel along −X; measured, not eyeballed.
    turn: { bishop: 90 },
  },
  /*
    The Second World War, two fronts drawn from one folder. In the west the USA
    is White, as ANZAC is in the first war: at Monte Cassino the Allies were
    the side attacking up the mountain. In the east, Russia. Germany is Black
    on both, so the same army looks the same wherever it is met.
  */
  ww2: { white: 'usa', black: 'german' },
  'ww2-east': { folder: 'ww2', white: 'russian', black: 'german' },
};

/** The folder a set's models are filed in. */
export function folderOf(set: string): string {
  return PIECE_SETS[set]?.folder ?? set;
}
